import { hexToRgb } from './colorUtils';

/**
 * Engraving Tint (editor preview only)
 *
 * Recolours the high-contrast black engraving render to the slot's configured
 * textColour, mirroring the server-side recolourInkStrokes (imageProcessor):
 * pixel darkness becomes opacity, then the alpha is filled with the colour.
 *
 * The tint is applied only to the on-screen canvas preview. Saved images stay
 * black: the group option (and therefore the colour) can change after upload,
 * outside the photo editor, so the visualiser recolours the black artwork
 * server-side with whichever slot colour is active at render time.
 */

/** Matches 3- or 6-digit hex colours, with or without leading '#'. */
export const isValidHexColour = (value?: string | null): value is string =>
  typeof value === 'string' && /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());

/** Rec601 luminance, matching the greyscale pass in processImage.ts */
const luminance = (r: number, g: number, b: number): number => 0.299 * r + 0.587 * g + 0.114 * b;

// Same noise stretch as the server's recolourInkStrokes linear(1.2, -15):
// pushes off-white paper/JPEG noise to zero alpha while keeping strokes opaque.
const NOISE_SCALE = 1.2;
const NOISE_OFFSET = 15;

/**
 * Tint RGBA pixel data in place: darkness -> alpha, filled with `colour`.
 * Output is colour strokes on transparency, composited over the step image
 * without multiply so light colours stay visible on dark products.
 */
export const tintPixels = (data: Uint8ClampedArray, colour: { r: number; g: number; b: number }): void => {
  for (let i = 0; i < data.length; i += 4) {
    // Flatten any existing alpha onto white before measuring darkness, the
    // same as the server's flatten({ background: '#ffffff' }).
    const coverage = data[i + 3] / 255;
    const flattenedLuminance = luminance(data[i], data[i + 1], data[i + 2]) * coverage + 255 * (1 - coverage);

    const darkness = 255 - flattenedLuminance;
    const alpha = Math.max(0, Math.min(255, darkness * NOISE_SCALE - NOISE_OFFSET));

    data[i] = colour.r;
    data[i + 1] = colour.g;
    data[i + 2] = colour.b;
    data[i + 3] = alpha;
  }
};

/**
 * Apply the engraving tint to a rendered preview canvas in place.
 * Call after all rendering (mask clipping, white background fills) is done,
 * immediately before exporting the canvas to a data URL.
 */
export const applyEngravingTint = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  hexColour: string
): void => {
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  tintPixels(imageData.data, hexToRgb(hexColour));
  ctx.putImageData(imageData, 0, 0);
};
